import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState, Loader } from "@/components/common";
import { SelectField, TextField } from "@/components/fields";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Package, Plus, GitDiff } from "@phosphor-icons/react";
import { toast } from "sonner";

const AREAS = {
  "Lounge/Dining": ["Painting", "Flooring", "Doors", "Keys/Locks", "Windows", "Lighting Fixtures", "Curtain box with rails", "TV socket", "Comments"],
  "Kitchen": ["Painting", "Flooring", "Doors", "Keys/Locks", "Windows", "Curtain box", "Lighting Fixtures", "Cooker Unit", "Drawers & cupboards", "Plumbing", "Kitchen Store", "Comments"],
  "Laundry Yard": ["Painting", "Flooring", "Windows", "Lighting Fixtures", "Plumbing", "Comments"],
  "Servants Quarter": ["Painting", "Flooring", "Doors", "Keys/Locks", "Windows", "Curtain box", "Lighting", "Wardrobe", "Dressing Mirror", "Plumbing", "WC pan", "Cistern", "Comments"],
  "Master Bedroom": ["Painting", "Flooring", "Doors", "Keys/Locks", "Windows", "Curtain box", "Lighting", "Bathtub/WC", "Wardrobe", "Dressing Mirror", "Plumbing", "Shower mixer", "Water Heater", "Comments"],
  "Bedroom 1": ["Painting", "Flooring", "Doors", "Keys/Locks", "Windows", "Curtain box", "Lighting", "Wardrobe", "Dressing Mirror", "WHB", "Plumbing", "WC pan", "Cistern", "Comments"],
  "Bedroom 2": ["Painting", "Flooring", "Doors", "Keys/Locks", "Windows", "Curtain box", "Lighting", "Wardrobe", "WHB", "Plumbing", "WC pan", "Cistern", "Comments"],
  "Guest Toilet": ["Painting", "Flooring", "Door", "Keys/Locks", "Windows", "Lighting", "Mirror", "WHB", "Plumbing", "WC pan", "Cistern"],
};

export default function Inventory() {
  const [items, setItems] = useState(null);
  const [props, setProps] = useState([]);
  const [open, setOpen] = useState(false);
  const [propertyId, setPropertyId] = useState("");
  const [type, setType] = useState("move_in");
  const [areas, setAreas] = useState({});
  const [meters, setMeters] = useState({ meter_electricity_no: "", meter_electricity_reading: "", meter_water_no: "", meter_water_reading: "" });
  const [compare, setCompare] = useState(null);

  const load = () => {
    api.get("/inventories").then((r) => setItems(r.data));
    api.get("/properties").then((r) => setProps(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const setItem = (area, item, val) => setAreas((a) => ({ ...a, [area]: { ...(a[area] || {}), [item]: val } }));

  const save = async () => {
    if (!propertyId) return toast.error("Select a property");
    try {
      await api.post("/inventories", { property_id: propertyId, inventory_type: type, areas, ...meters });
      toast.success("Inventory saved"); setOpen(false); setAreas({}); setPropertyId("");
      setMeters({ meter_electricity_no: "", meter_electricity_reading: "", meter_water_no: "", meter_water_reading: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const runCompare = async (pid) => {
    try { const r = await api.get(`/inventories/compare/${pid}`); setCompare(r.data); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!items) return <Loader />;

  return (
    <div>
      <PageHeader title="Inventory" subtitle="Move-in / move-out inventory & damage comparison" icon={Package}
        actions={<Button onClick={() => setOpen(true)} data-testid="add-inventory-btn" className="gap-2"><Plus size={16} /> New Inventory</Button>} />

      {items.length === 0 ? (
        <EmptyState icon={Package} title="No inventories yet" message="Capture a move-in inventory at tenant handover."
          action={<Button onClick={() => setOpen(true)}><Plus size={16} className="mr-2" />New Inventory</Button>} />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Property</TableHead><TableHead>Type</TableHead><TableHead>Areas</TableHead><TableHead>Date</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map((inv) => (
                  <TableRow key={inv.id} data-testid={`inventory-row-${inv.id}`}>
                    <TableCell className="font-medium">{inv.property_name}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{inv.inventory_type.replace(/_/g, " ")}</Badge></TableCell>
                    <TableCell className="text-sm">{Object.keys(inv.areas || {}).length} areas</TableCell>
                    <TableCell className="text-sm">{fmtDate(inv.created_at)}</TableCell>
                    <TableCell className="text-right"><Button size="sm" variant="outline" className="gap-1" onClick={() => runCompare(inv.property_id)}><GitDiff size={15} /> Compare</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Inventory</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <SelectField label="Property" value={propertyId} onChange={setPropertyId}
                options={props.map((p) => ({ value: p.id, label: `${p.name} ${p.unit_no || ""}` }))} required testId="inventory-property" />
              <SelectField label="Type" value={type} onChange={setType} options={[{ value: "move_in", label: "Move-In" }, { value: "move_out", label: "Move-Out" }]} />
            </div>
            <Accordion type="multiple" className="border rounded-md">
              {Object.entries(AREAS).map(([area, list]) => (
                <AccordionItem key={area} value={area}>
                  <AccordionTrigger className="px-4 text-sm">{area}</AccordionTrigger>
                  <AccordionContent className="px-4">
                    <div className="grid sm:grid-cols-2 gap-2">
                      {list.map((item) => (
                        <div key={item} className="flex items-center gap-2">
                          <span className="text-xs w-28 shrink-0 text-muted-foreground">{item}</span>
                          <Input className="h-8" value={areas[area]?.[item] || ""} onChange={(e) => setItem(area, item, e.target.value)} placeholder="Condition…" />
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <div className="grid sm:grid-cols-2 gap-4">
              <TextField label="Electricity Meter No." value={meters.meter_electricity_no} onChange={(v) => setMeters({ ...meters, meter_electricity_no: v })} />
              <TextField label="Electricity Reading" value={meters.meter_electricity_reading} onChange={(v) => setMeters({ ...meters, meter_electricity_reading: v })} />
              <TextField label="Water Meter No." value={meters.meter_water_no} onChange={(v) => setMeters({ ...meters, meter_water_no: v })} />
              <TextField label="Water Reading" value={meters.meter_water_reading} onChange={(v) => setMeters({ ...meters, meter_water_reading: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-inventory-btn">Save Inventory</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!compare} onOpenChange={() => setCompare(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Move-In vs Move-Out Comparison</DialogTitle></DialogHeader>
          {!compare?.move_in || !compare?.move_out ? (
            <p className="text-sm text-muted-foreground">Both move-in and move-out inventories are required to compare.</p>
          ) : compare.differences.length === 0 ? (
            <p className="text-sm text-primary">No differences detected — property returned in original condition.</p>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>Area</TableHead><TableHead>Item</TableHead><TableHead>Move-In</TableHead><TableHead>Move-Out</TableHead></TableRow></TableHeader>
              <TableBody>
                {compare.differences.map((d, i) => (
                  <TableRow key={i}><TableCell>{d.area}</TableCell><TableCell>{d.item}</TableCell>
                    <TableCell className="text-sm">{d.move_in || "—"}</TableCell>
                    <TableCell className="text-sm text-destructive">{d.move_out || "—"}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
